import { Link } from 'react-router-dom';
import { BookOpen, ClipboardList, FolderCheck, ArrowRight, AlertTriangle, Check, X } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const slug = (s) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-');

const toneClass = (tone) =>
  tone === 'warn' ? 'text-[#B45309]' : tone === 'bad' ? 'text-[#B91C1C]' : 'text-[#143A34]';

function MissionStrip({ mission, idPrefix }) {
  return (
    <div
      className="border border-[#E5E7EB] border-l-2 border-l-[#2A9B68] bg-white px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-1"
      data-testid={`${idPrefix}-strip`}
    >
      <span className="text-[10.5px] uppercase tracking-[0.12em] font-semibold text-[#2A9B68]">
        {mission.step ? `Paso ${mission.step} de 7` : 'Evidencia'}
      </span>
      <span className="text-[14px] font-semibold text-[#1C2329]">{mission.title}</span>
      <span className="text-[12px] text-[#6B7280]">{mission.legalRef}</span>
    </div>
  );
}

function Prereq({ prereq, idPrefix }) {
  return (
    <div
      className="border border-[#F59E0B]/40 bg-[#FFFBEB] px-4 py-3 flex flex-wrap items-center justify-between gap-3"
      data-testid={`${idPrefix}-prereq`}
    >
      <div className="flex items-start gap-2 text-[13px] text-[#92400E]">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        <span>{prereq.message}</span>
      </div>
      <Link
        to={prereq.to}
        className="text-[12.5px] font-medium text-[#92400E] border border-[#F59E0B]/60 bg-white px-3 py-1.5 inline-flex items-center gap-1 hover:bg-[#FEF3C7] transition-colors"
        data-testid={`${idPrefix}-prereq-link`}
      >
        {prereq.cta} <ArrowRight size={12} />
      </Link>
    </div>
  );
}

function ListBlock({ title, items, icon: Icon, color, testid }) {
  return (
    <div className="border border-[#E5E7EB] bg-white p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider font-medium text-[#6B7280]">{title}</div>
      <ul className="mt-2 space-y-1.5">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-2 text-[13px] text-[#374151] leading-relaxed">
            <Icon size={13} className="mt-1 shrink-0" style={{ color }} strokeWidth={2} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LearnTab({ mission, idPrefix }) {
  return (
    <div className="space-y-3" data-testid={`${idPrefix}-learn`}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {mission.learn.map((l) => (
          <div key={l.title} className="border border-[#E5E7EB] bg-white p-4">
            <div className="text-[11px] uppercase tracking-wider font-medium text-[#2A9B68]">{l.title}</div>
            <p className="text-[13px] text-[#374151] leading-relaxed mt-1.5">{l.body}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ListBlock title="Ejemplos en una empresa" items={mission.examples} icon={Check} color="#2A9B68" testid={`${idPrefix}-examples`} />
        <ListBlock title="Errores comunes" items={mission.mistakes} icon={X} color="#DC2626" testid={`${idPrefix}-mistakes`} />
      </div>
    </div>
  );
}

function EvidencePanel({ evidence, note, idPrefix }) {
  return (
    <div className="border border-[#E5E7EB] bg-white" data-testid={`${idPrefix}-evidence`}>
      <div className="px-4 py-3 border-b border-[#E5E7EB] flex flex-wrap items-center justify-between gap-2">
        <div className="text-[13px] font-medium text-[#1C2329]">Evidencia lista para una fiscalización</div>
        <Link
          to="/app/fiscalizacion"
          className="text-[12.5px] text-[#143A34] hover:underline inline-flex items-center gap-1"
          data-testid={`${idPrefix}-evidence-link`}
        >
          Abrir Carpeta de evidencias <ArrowRight size={12} />
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-[#E5E7EB]">
        {evidence.map((e) => (
          <div key={e.label} className="px-4 py-3" data-testid={`${idPrefix}-evidence-${slug(e.label)}`}>
            <div className="text-[11px] uppercase tracking-wider font-medium text-[#6B7280]">{e.label}</div>
            <div className={`text-[16px] font-semibold mt-0.5 tabular-nums ${toneClass(e.tone)}`}>{e.value}</div>
            {e.detail && <div className="text-[12px] text-[#6B7280] mt-0.5">{e.detail}</div>}
          </div>
        ))}
      </div>
      {note && <div className="px-4 py-2.5 border-t border-[#E5E7EB] text-[12px] text-[#6B7280]">{note}</div>}
    </div>
  );
}

export default function MissionShell({ mission, idPrefix, doHint, prereq, evidence = [], evidenceNote, children }) {
  return (
    <div className="space-y-5" data-testid={`${idPrefix}-shell`}>
      <MissionStrip mission={mission} idPrefix={idPrefix} />
      {prereq && <Prereq prereq={prereq} idPrefix={idPrefix} />}
      <Tabs defaultValue="hazlo">
        <TabsList className="border border-[#E5E7EB] bg-[#F3F4F6]" data-testid={`${idPrefix}-tabs`}>
          <TabsTrigger value="aprende" className="gap-1.5" data-testid={`${idPrefix}-tab-aprende`}>
            <BookOpen size={13} strokeWidth={1.5} /> Aprende
          </TabsTrigger>
          <TabsTrigger value="hazlo" className="gap-1.5" data-testid={`${idPrefix}-tab-hazlo`}>
            <ClipboardList size={13} strokeWidth={1.5} /> Hazlo
          </TabsTrigger>
          <TabsTrigger value="demuestralo" className="gap-1.5" data-testid={`${idPrefix}-tab-demuestralo`}>
            <FolderCheck size={13} strokeWidth={1.5} /> Demuéstralo
          </TabsTrigger>
        </TabsList>
        <TabsContent value="aprende" className="mt-4">
          <LearnTab mission={mission} idPrefix={idPrefix} />
        </TabsContent>
        <TabsContent value="hazlo" className="mt-4 space-y-4">
          {doHint && (
            <div
              className="border border-[#2A9B68]/25 bg-[#F0FDF4] px-3.5 py-2.5 text-[12.5px] text-[#065F46] flex items-start gap-2"
              data-testid={`${idPrefix}-next-action`}
            >
              <ArrowRight size={13} className="mt-0.5 shrink-0" />
              <span>{doHint}</span>
            </div>
          )}
          {children}
        </TabsContent>
        <TabsContent value="demuestralo" className="mt-4">
          <EvidencePanel evidence={evidence} note={evidenceNote} idPrefix={idPrefix} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
